
import { useConfig } from '@/context/ConfigContext';
import { SettingsValueMap, SettingType } from '@/types/config';
import { useState, useEffect } from 'react';

/**
 * Hook for settings panel functionality - completely dynamic
 * Provides easy access to settings configuration and values
 */
export const useSettingsPanel = () => {
  const { config, getSettingsValueMap, getSettingsValueMapWithStorage } = useConfig();
  const [, setRefresh] = useState(0);

  // Listen for settings updates to trigger re-renders
  useEffect(() => {
    const handleSettingsUpdate = () => {
      setRefresh(prev => prev + 1);
    };

    window.addEventListener('settings-updated', handleSettingsUpdate);
    return () => window.removeEventListener('settings-updated', handleSettingsUpdate);
  }, []);

  /**
   * Get the settings value map (flat key-value pairs)
   */
  const getSettingsMap = (): SettingsValueMap => {
    return getSettingsValueMapWithStorage();
  };

  /**
   * Get a specific setting value by key
   */
  const getSettingValue = (key: string): boolean => {
    const valueMap = getSettingsValueMapWithStorage();
    return valueMap[key] ?? false;
  };

  /**
   * Check if a setting is enabled/true
   */
  const isSettingEnabled = (key: string): boolean => {
    return getSettingValue(key);
  };

  /**
   * Get all visible tabs - completely dynamic
   */
  const getVisibleTabs = (): string[] => {
    const settingsPanel = config.settingsPanel as Record<string, { visible?: boolean }>;
    if (!settingsPanel) return [];

    return Object.entries(settingsPanel)
      .filter(([_, tab]) => tab && typeof tab === 'object' && tab.visible !== false)
      .map(([tabName, _]) => tabName);
  };

  /**
   * Get tab labels with their names - completely dynamic
   */
  const getTabLabels = (): Array<{ name: string; label: string }> => {
    const settingsPanel = config.settingsPanel as Record<string, { visible?: boolean; label?: string }>;
    if (!settingsPanel) return [];

    return Object.entries(settingsPanel)
      .filter(([_, tab]) => tab && typeof tab === 'object' && tab.visible !== false)
      .map(([tabName, tab]) => ({
        name: tabName,
        label: tab.label || tabName.charAt(0).toUpperCase() + tabName.slice(1) // Fallback to capitalized name
      }));
  };

  /**
   * Get label for a specific tab - completely dynamic
   */
  const getTabLabel = (tabName: string): string => {
    const settingsPanel = config.settingsPanel as Record<string, { visible?: boolean; label?: string }>;
    const tab = settingsPanel?.[tabName];
    
    if (tab && typeof tab === 'object' && tab.label) {
      return tab.label;
    }
    
    // Fallback to capitalized tab name
    return tabName.charAt(0).toUpperCase() + tabName.slice(1);
  };

  /**
   * Get all settings for a specific tab - completely dynamic
   */
  const getTabSettings = (tabName: string): Record<string, unknown> => {
    const settingsPanel = config.settingsPanel as Record<string, Record<string, unknown>>;
    const tab = settingsPanel?.[tabName];
    if (!tab || typeof tab === 'boolean') return {};

    const settings: Record<string, unknown> = {};
    Object.entries(tab).forEach(([key, value]) => {
      if (key !== 'visible' && typeof value === 'object' && value !== null) {
        settings[key] = value;
      }
    });

    return settings;
  };

  /**
   * Get all settings with their current values - completely dynamic
   */
  const getAllSettingsWithValues = (): Array<{
    type: SettingType;
    label: string;
    icon?: string;
    value: boolean;
    tabName: string;
    settingKey: string;
    data?: Record<string, { label: string; value?: boolean }>;
  }> => {
    const settingsWithValues: Array<{
      type: SettingType;
      label: string;
      icon?: string;
      value: boolean;
      tabName: string;
      settingKey: string;
      data?: Record<string, { label: string; value?: boolean }>;
    }> = [];
    const valueMap = getSettingsValueMapWithStorage();

    const settingsPanel = config.settingsPanel as Record<string, Record<string, unknown>>;
    if (!settingsPanel) return settingsWithValues;

    Object.entries(settingsPanel).forEach(([tabName, tab]) => {
      if (!tab || typeof tab === 'boolean') return;

      Object.entries(tab).forEach(([settingKey, setting]) => {
        if (settingKey === 'visible' || typeof setting === 'boolean') return;

        if (setting && typeof setting === 'object' && 'type' in setting) {
          const typedSetting = setting as {
            type: SettingType;
            label: string;
            icon?: string;
            defaultValue?: boolean;
            data?: Record<string, { label: string; value?: boolean }>;
          };

          switch (typedSetting.type) {
            case SettingType.TOGGLE:
              settingsWithValues.push({
                type: typedSetting.type,
                label: typedSetting.label,
                icon: typedSetting.icon,
                value: valueMap[settingKey] ?? typedSetting.defaultValue ?? false,
                tabName,
                settingKey
              });
              break;
              
            case SettingType.SELECT:
              // For SELECT settings, create entries for each option
              if (typedSetting.data) {
                Object.entries(typedSetting.data).forEach(([selectName, option]) => {
                  settingsWithValues.push({
                    type: typedSetting.type,
                    label: typedSetting.label,
                    icon: typedSetting.icon,
                    value: valueMap[selectName] ?? option.value ?? false,
                    tabName,
                    settingKey: selectName,
                    data: typedSetting.data
                  });
                });
              }
              break;
          }
        }
      });
    });

    return settingsWithValues;
  };

  /**
   * Get settings by type - completely dynamic
   */
  const getSettingsByType = (type: SettingType) => {
    const allSettings = getAllSettingsWithValues();
    return allSettings.filter(setting => setting.type === type);
  };

  /**
   * Get toggle settings - completely dynamic
   */
  const getToggleSettings = () => {
    return getSettingsByType(SettingType.TOGGLE);
  };

  /**
   * Get select settings - completely dynamic
   */
  const getSelectSettings = () => {
    return getSettingsByType(SettingType.SELECT);
  };

  /**
   * Get any value from config using dot notation - completely dynamic
   */
  const getConfigValue = (path: string): unknown => {
    return path.split('.').reduce((obj, key) => {
      if (obj && typeof obj === 'object' && key in obj) {
        return (obj as Record<string, unknown>)[key];
      }
      return undefined;
    }, config as unknown);
  };

  /**
   * Check if any feature is enabled using dot notation - completely dynamic
   */
  const isFeatureEnabled = (path: string): boolean => {
    const value = getConfigValue(path);
    return Boolean(value);
  };

  return {
    // Core functionality
    getSettingsMap,
    getSettingValue,
    isSettingEnabled,
    
    // Tab management
    getVisibleTabs,
    getTabLabels,
    getTabLabel,
    getTabSettings,
    
    // Settings retrieval
    getAllSettingsWithValues,
    getSettingsByType,
    
    // Type-specific getters
    getToggleSettings,
    getSelectSettings,
    
    // Dynamic access
    getConfigValue,
    isFeatureEnabled,
    
    // Raw access
    settingsPanel: config.settingsPanel
  };
};
